import { useCallback, useState } from 'react';
import {
  INITIAL_FILE_PROMPT,
  INITIAL_PROMPT,
  INITIAL_TEXT_PROMPT,
} from '@/features/magic/constants';
import type { Prompt } from '@/features/magic/types';
import { cleanPrompt } from '@/features/magic/utils/prompt';
import { toBase64FileInput } from '@/utils/file';

export const usePrompt = () => {
  const [prompt, _setPrompt] = useState<Prompt>(INITIAL_PROMPT);

  const setPrompt = useCallback((value: Partial<Prompt>) => {
    const file = value.file;

    _setPrompt((prevPrompt) =>
      cleanPrompt({
        ...prevPrompt,
        ...value,
        // Any change to `file` invalidates whatever base64 was read for
        // whatever file used to be there — including a clear (`file: null`).
        ...('file' in value ? { fileBase64: null } : {}),
      })
    );

    if (file instanceof File) {
      // Read now, while the picked file's handle is freshest, rather than
      // waiting for submit — on iOS that handle can go stale the longer the
      // app waits to read it (WEB-CLIENT-28X). The functional update below
      // only applies if `file` is still the prompt's current file by the
      // time the read resolves, so a stale read for a since-replaced file
      // can't clobber state; a rejection just leaves `fileBase64` unset for
      // `useCreateMagicJob` to read the file itself as its fallback.
      toBase64FileInput(file)
        .then((fileBase64) => {
          _setPrompt((prevPrompt) =>
            prevPrompt.file === file
              ? { ...prevPrompt, fileBase64 }
              : prevPrompt
          );
        })
        .catch(() => {});
    }
  }, []);

  const resetPrompt = useCallback(() => {
    setPrompt(INITIAL_PROMPT);
  }, [setPrompt]);

  const clearTextPrompt = useCallback(() => {
    setPrompt(INITIAL_TEXT_PROMPT);
  }, [setPrompt]);

  const clearFilePrompt = useCallback(() => {
    setPrompt(INITIAL_FILE_PROMPT);
  }, [setPrompt]);

  return {
    prompt,
    setPrompt,
    resetPrompt,
    clearTextPrompt,
    clearFilePrompt,
  };
};
